import React from "react"

/**
 * Draft.js raw content block (from convertToRaw).
 * Use this type when you need to work with parsed blocks.
 */
export interface DraftBlock {
  key: string
  text: string
  type: string
  depth?: number
  inlineStyleRanges?: Array<{ offset: number; length: number; style: string }>
  entityRanges?: Array<{ offset: number; length: number; key: number | string }>
  data?: Record<string, unknown>
}

export interface DraftEntity {
  type: string
  data?: Record<string, unknown>
}

export interface DraftRawParsed {
  blocks: DraftBlock[]
  entityMap: Record<string, DraftEntity>
}

/**
 * Parses a string that may be Draft.js raw JSON (convertToRaw output).
 * @param raw - The raw string (e.g. from API: admin_propertyDescription).
 * @returns { blocks, entityMap } if valid Draft raw, otherwise null.
 */
export function parseDraftRaw(raw: string): DraftRawParsed | null {
  if (!raw || typeof raw !== "string") return null
  const trimmed = raw.trim()
  if (!trimmed.startsWith("{")) return null
  try {
    const parsed = JSON.parse(raw) as { blocks?: DraftBlock[]; entityMap?: Record<string, DraftEntity> }
    if (Array.isArray(parsed?.blocks) && parsed.blocks.length > 0) {
      return {
        blocks: parsed.blocks,
        entityMap: parsed.entityMap && typeof parsed.entityMap === "object" ? parsed.entityMap : {},
      }
    }
  } catch {
    /* not JSON */
  }
  return null
}

function getStyleForSegment(styles: string[]): React.CSSProperties & { bold?: boolean; italic?: boolean } {
  const style: React.CSSProperties & { bold?: boolean; italic?: boolean } = {}
  for (const s of styles) {
    if (s === "BOLD") style.bold = true
    else if (s === "ITALIC") style.italic = true
    else if (s.startsWith("fontsize-")) {
      const v = s.replace("fontsize-", "").trim()
      style.fontSize = v.endsWith("rem") ? v : `${v}px`
    } else if (s.startsWith("color-rgb(")) {
      const m = s.match(/color-rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
      if (m) style.color = `rgb(${m[1]},${m[2]},${m[3]})`
    } else if (s.startsWith("bgcolor-rgb(")) {
      const m = s.match(/bgcolor-rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
      if (m) style.backgroundColor = `rgb(${m[1]},${m[2]},${m[3]})`
    }
  }
  return style
}

/** Work in character (code point) space so emojis and multi-unit chars don't truncate text. */
function applyInlineStyles(text: string, ranges: DraftBlock["inlineStyleRanges"]): React.ReactNode {
  if (!text) return null
  if (!ranges?.length) return text
  const chars = Array.from(text)
  const len = chars.length
  const styleAt: string[][] = []
  for (let i = 0; i < len; i++) styleAt[i] = []
  for (const r of ranges) {
    if (r.offset < 0 || r.length <= 0 || r.offset + r.length > len) continue
    for (let i = r.offset; i < r.offset + r.length; i++) styleAt[i].push(r.style)
  }
  const segments: Array<{ start: number; end: number; styles: string[] }> = []
  let start = 0
  let prevKey = styleAt[0]?.slice().sort().join(",") ?? ""
  for (let i = 1; i <= len; i++) {
    const key = i < len ? (styleAt[i]?.slice().sort().join(",") ?? "") : ""
    if (key !== prevKey) {
      segments.push({ start, end: i, styles: (styleAt[start] ?? []).filter((s, idx, arr) => arr.indexOf(s) === idx) })
      start = i
      prevKey = key
    }
  }
  if (segments.length === 0) return text
  return (
    <>
      {segments.map((seg, idx) => {
        const segText = chars.slice(seg.start, seg.end).join("")
        const st = getStyleForSegment(seg.styles)
        const { bold, italic, ...css } = st
        let node: React.ReactNode = segText
        if (Object.keys(css).length > 0) node = <span key={idx} style={css}>{node}</span>
        if (italic) node = <em key={`${idx}-i`}>{node}</em>
        if (bold) node = <strong key={`${idx}-b`}>{node}</strong>
        return <React.Fragment key={idx}>{node}</React.Fragment>
      })}
    </>
  )
}

/** Inline styles for a segment of text (ranges adjusted to segment). Used when block has entities. */
function applyInlineStylesToSegment(
  fullChars: string[],
  ranges: DraftBlock["inlineStyleRanges"],
  segStart: number,
  segEnd: number
): React.ReactNode {
  const segText = fullChars.slice(segStart, segEnd).join("")
  if (!segText) return null
  if (!ranges?.length) return segText
  const segLen = segEnd - segStart
  const styleAt: string[][] = []
  for (let i = 0; i < segLen; i++) styleAt[i] = []
  for (const r of ranges) {
    const rEnd = r.offset + r.length
    if (rEnd <= segStart || r.offset >= segEnd) continue
    const start = Math.max(0, r.offset - segStart)
    const end = Math.min(segLen, rEnd - segStart)
    for (let i = start; i < end; i++) styleAt[i].push(r.style)
  }
  const segments: Array<{ start: number; end: number; styles: string[] }> = []
  let start = 0
  let prevKey = styleAt[0]?.slice().sort().join(",") ?? ""
  for (let i = 1; i <= segLen; i++) {
    const key = i < segLen ? (styleAt[i]?.slice().sort().join(",") ?? "") : ""
    if (key !== prevKey) {
      segments.push({ start, end: i, styles: (styleAt[start] ?? []).filter((s, idx, arr) => arr.indexOf(s) === idx) })
      start = i
      prevKey = key
    }
  }
  if (segments.length === 0) return segText
  return (
    <>
      {segments.map((seg, idx) => {
        const sub = fullChars.slice(segStart + seg.start, segStart + seg.end).join("")
        const st = getStyleForSegment(seg.styles)
        const { bold, italic, ...css } = st
        let node: React.ReactNode = sub
        if (Object.keys(css).length > 0) node = <span key={idx} style={css}>{node}</span>
        if (italic) node = <em key={`${idx}-i`}>{node}</em>
        if (bold) node = <strong key={`${idx}-b`}>{node}</strong>
        return <React.Fragment key={idx}>{node}</React.Fragment>
      })}
    </>
  )
}

function renderEntity(entity: DraftEntity, segmentText: string, key: string | number): React.ReactNode {
  if (!entity) return segmentText
  const type = (entity.type || "").toUpperCase()
  const data = entity.data || {}
  if (type === "IMAGE") {
    const src = data.src ?? data.url
    if (!src) return null
    return (
      <img
        key={key}
        src={typeof src === "string" ? src : ""}
        alt={(data.alt as string) ?? ""}
        className="draft-raw-image"
        style={{ maxWidth: "100%", height: "auto", verticalAlign: "middle", margin: "4px 0" }}
      />
    )
  }
  if (type === "LINK") {
    const url = data.url ?? data.href
    const href = typeof url === "string" && url.trim().length > 0 ? url.trim() : null
    const label = segmentText || (typeof url === "string" ? url : "") || "Link"
    if (href && (href.startsWith("http://") || href.startsWith("https://") || href.startsWith("/"))) {
      return (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      )
    }
    return <span key={key}>{label}</span>
  }
  return segmentText
}

/** Renders block text + entities (images, links). Emojis stay in text. */
function blockContentWithEntities(block: DraftBlock, entityMap: Record<string, DraftEntity>): React.ReactNode {
  const chars = Array.from(block.text)
  const ranges = block.entityRanges
  if (!ranges?.length) return applyInlineStyles(block.text, block.inlineStyleRanges)
  const sorted = [...ranges].sort((a, b) => a.offset - b.offset)
  const parts: React.ReactNode[] = []
  let lastEnd = 0
  sorted.forEach((er, idx) => {
    if (er.offset > lastEnd) {
      parts.push(
        <React.Fragment key={`t-${idx}`}>
          {applyInlineStylesToSegment(chars, block.inlineStyleRanges, lastEnd, er.offset)}
        </React.Fragment>
      )
    }
    const entity = entityMap[String(er.key)]
    const segText = chars.slice(er.offset, er.offset + er.length).join("")
    parts.push(
      <React.Fragment key={`e-${er.key}-${idx}`}>
        {renderEntity(entity, segText, `${block.key}-${er.key}-${idx}`)}
      </React.Fragment>
    )
    lastEnd = er.offset + er.length
  })
  if (lastEnd < chars.length) {
    parts.push(
      <React.Fragment key="t-end">
        {applyInlineStylesToSegment(chars, block.inlineStyleRanges, lastEnd, chars.length)}
      </React.Fragment>
    )
  }
  return <>{parts}</>
}

/**
 * Renders an array of Draft blocks as React nodes (headings, paragraphs, lists).
 * Images and links from entityMap are rendered; emojis in text are preserved.
 */
export function renderDraftBlocks(
  blocks: DraftBlock[],
  className?: string,
  entityMap: Record<string, DraftEntity> = {}
): React.ReactNode {
  const items: React.ReactNode[] = []
  let i = 0
  while (i < blocks.length) {
    const b = blocks[i]
    const tag = b.type === "header-one" ? "h1" : b.type === "header-two" ? "h2" : b.type === "header-three" ? "h3" : b.type === "header-four" ? "h4" : b.type === "unstyled" || b.type === "paragraph" ? "p" : null
    const content = () => blockContentWithEntities(b, entityMap)
    if (b.type === "unordered-list-item") {
      const listItems: DraftBlock[] = []
      while (i < blocks.length && blocks[i].type === "unordered-list-item") listItems.push(blocks[i++])
      items.push(
        <ul key={b.key} className="mb-2 ps-3">
          {listItems.map((li) => (
            <li key={li.key}>{blockContentWithEntities(li, entityMap)}</li>
          ))}
        </ul>
      )
      continue
    }
    if (b.type === "ordered-list-item") {
      const listItems: DraftBlock[] = []
      while (i < blocks.length && blocks[i].type === "ordered-list-item") listItems.push(blocks[i++])
      items.push(
        <ol key={b.key} className="mb-2 ps-3">
          {listItems.map((li) => (
            <li key={li.key}>{blockContentWithEntities(li, entityMap)}</li>
          ))}
        </ol>
      )
      continue
    }
    if (tag) {
      const node = content()
      if (tag === "p") items.push(<p key={b.key} className="mb-2">{node}</p>)
      else if (tag === "h1") items.push(<h1 key={b.key} className="h5 mb-2">{node}</h1>)
      else if (tag === "h2") items.push(<h2 key={b.key} className="h5 mb-2">{node}</h2>)
      else if (tag === "h3") items.push(<h3 key={b.key} className="h6 mb-2">{node}</h3>)
      else items.push(<h4 key={b.key} className="h6 mb-2">{node}</h4>)
    }
    i++
  }
  return <div className={className ?? "draft-rich-content"}>{items}</div>
}

/**
 * Decodes Draft.js raw string to React content. Reusable everywhere.
 * - If the string is valid Draft raw JSON (with blocks), returns rendered rich content.
 * - Otherwise returns the raw string wrapped in a single paragraph (or null if empty).
 *
 * @example
 * // In any component:
 * import { decodeDraftRawToReact } from "helpers/draftRawToReact"
 * ...
 * <div className="card-body">{decodeDraftRawToReact(description)}</div>
 */
export function decodeDraftRawToReact(raw: string): React.ReactNode {
  if (!raw || typeof raw !== "string") return null
  const parsed = parseDraftRaw(raw)
  if (parsed) return renderDraftBlocks(parsed.blocks, undefined, parsed.entityMap)
  return <p className="mb-0">{raw}</p>
}
